import * as React from 'react';
import {
  root as rootCls,
  buttonStack as stackCls,
  divider as dividerCls,
  railButton as railBtnCls,
} from './IconRail.css';

export interface IconRailProps extends React.HTMLAttributes<HTMLElement> {
  ref?: React.Ref<HTMLElement>;
}

/**
 * IconRail — fixed-width 48px vertical rail. The dark navigation
 * column on the left of the workspace shell. Children are typically
 * `IconRail.Button` instances; render `IconRail.Divider` between
 * groups.
 *
 * Prototype: `http://localhost:8000/` left rail. Validated 2026-04-30.
 */
export function IconRail({ children, ref, className, ...rest }: IconRailProps) {
  const cls = [rootCls, className].filter(Boolean).join(' ');
  return (
    <nav ref={ref} className={cls} aria-label="Workspace navigation" {...rest}>
      <div className={stackCls}>{children}</div>
    </nav>
  );
}
IconRail.displayName = 'IconRail';

export interface IconRailButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Required accessible label — icon-only button. */
  'aria-label': string;
  /**
   * Whether this button's panel is currently open (expanded). Drives both
   * the active visual treatment and `aria-expanded`.
   */
  active?: boolean;
  /**
   * Icon child — typically a 20px glyph from the DS Icon registry rendered
   * via `<Icon as={Folder} size={20} />` (`primitives/Icon/icons`), never a
   * bare `lucide-react` import.
   */
  icon: React.ReactNode;
  ref?: React.Ref<HTMLButtonElement>;
}

/**
 * RailButton — a single icon-only rail trigger.
 *
 * A11y model: **disclosure**, not toggle-button. Each rail button opens a
 * mutually-exclusive `<RailPanel>` to its right and collapses it on a
 * second click, so it carries `aria-expanded` (the disclosure pattern),
 * NOT `aria-pressed` (which models an independent on/off toggle and would
 * mislead AT into announcing five unrelated "pressed/not pressed" states
 * for what is really "which one panel is open"). The button names its
 * controlled panel with `aria-controls` when the consumer wires the panel's
 * id via the standard `aria-controls` attribute (passed through `...rest`).
 */
export function RailButton({
  icon,
  active = false,
  ref,
  className,
  type = 'button',
  ...rest
}: IconRailButtonProps) {
  const cls = [railBtnCls({ active }), className].filter(Boolean).join(' ');
  return (
    <button ref={ref} type={type} className={cls} aria-expanded={active} {...rest}>
      {icon}
    </button>
  );
}
RailButton.displayName = 'IconRail.Button';

export function Divider({
  ref,
  className,
  ...rest
}: React.HTMLAttributes<HTMLSpanElement> & { ref?: React.Ref<HTMLSpanElement> }) {
  const cls = [dividerCls, className].filter(Boolean).join(' ');
  return <span ref={ref} role="separator" className={cls} {...rest} />;
}
Divider.displayName = 'IconRail.Divider';

IconRail.Button = RailButton;
IconRail.Divider = Divider;
